import React, { useEffect, useRef, useState } from 'react';
import { Spin } from 'antd';
import { FilePdfOutlined, FileUnknownOutlined, AudioOutlined, WarningOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import mobileService from '../../../../services/mobileService';

/**
 * Vignette d'une piece deposee depuis le mobile.
 *
 * Les images sont reellement rendues : sur une declaration, l'agent trie d'abord
 * a l'oeil (une photo de degat, une photo de carte grise, un flou inexploitable),
 * et une liste de noms de fichiers ne permet pas ce tri. Le blob passe par l'API
 * authentifiee - un <img src> vers la GED n'enverrait pas le token.
 *
 * Les autres formats (PDF, audio) n'ont pas d'apercu : une icone suffit, le clic
 * ouvre la previsualisation ou le lecteur.
 */
export default function VignetteDocument({ doc, taille = 84 }) {
  const { t } = useTranslation('dossierSinistre');
  const [blobUrl, setBlobUrl] = useState(null);
  const [echec, setEchec] = useState(false);
  const urlRef = useRef(null);

  const mime = doc?.mimeType || '';
  const estImage = mime.startsWith('image/');
  const estPdf = mime.includes('pdf');
  const estAudio = mime.startsWith('audio/');
  const gedId = doc?.ossanGedDocumentId;

  useEffect(() => {
    if (!estImage || !gedId) return undefined;
    let annule = false;

    mobileService.telechargerDocument(gedId)
      .then((blob) => {
        if (annule) return;
        const url = URL.createObjectURL(blob);
        urlRef.current = url;
        setBlobUrl(url);
      })
      .catch(() => { if (!annule) setEchec(true); });

    return () => {
      annule = true;
      if (urlRef.current) {
        URL.revokeObjectURL(urlRef.current);
        urlRef.current = null;
      }
    };
  }, [estImage, gedId]);

  const cadre = {
    width: taille,
    height: taille,
    borderRadius: 8,
    border: '1px solid #e5e7eb',
    background: '#f9fafb',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    flexShrink: 0,
  };

  if (estImage) {
    // Pas d'identifiant GED : indexation en cours ou repli local. Sans ce cas, le
    // spinner tournerait indefiniment sur une image qu'on n'ira jamais chercher.
    if (echec || !gedId) {
      return (
        <div style={cadre} title={t('vignetteDocument.apercuIndisponible')}>
          <WarningOutlined style={{ fontSize: 22, color: '#d97706' }} />
        </div>
      );
    }
    return (
      <div style={cadre}>
        {blobUrl
          ? <img src={blobUrl} alt={doc?.titre || t('vignetteDocument.pieceJointe')} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <Spin size="small" />}
      </div>
    );
  }

  const icone = estPdf
    ? <FilePdfOutlined style={{ fontSize: 26, color: '#b91c1c' }} />
    : estAudio
      ? <AudioOutlined style={{ fontSize: 26, color: '#7c3aed' }} />
      : <FileUnknownOutlined style={{ fontSize: 26, color: '#9ca3af' }} />;

  return <div style={cadre}>{icone}</div>;
}
